import React, { useContext, useLayoutEffect, useRef, useState } from "react";

import { Preview } from "@creatomate/preview";
import { classnames } from "@culturehq/components";

import { DragProvider } from "./context/DragContext";
import { TrimProvider } from "./context/TrimContext";
import SelectableElements from "./SelectableElements";
import TimelineContainer from "./TimelineContainer";
import { ThumbnailProvider } from "./ThumbnailsContext";
import { CreatomateContext } from "../contexts/VideoStudioContext";
import { IMAGE_TYPE, VIDEO_TYPE } from "../utils/constants";
import TimelineRuler from "./TimelineRuler";

const MIN_TIMELINE_WIDTH = 600;
const TOOLS_HEIGHT = 150;

interface TimelineProps {
  draggablePositionY: number;
  preview: Preview;
  timelineHeight: number;
}

const Timeline: React.FC<TimelineProps> = ({
  draggablePositionY, preview, timelineHeight
}) => {
  const { data: { mediaType } } = useContext(CreatomateContext);

  const containerRef = useRef<HTMLDivElement>(null);
  const timelineContainer = useRef<HTMLDivElement>(null);
  const [timelineWidth, setTimelineWidth] = useState(MIN_TIMELINE_WIDTH);

  useLayoutEffect(
    () => {
      const updateSize = () => {
        setTimelineWidth(timelineContainer.current?.clientWidth || MIN_TIMELINE_WIDTH);
      };
      setTimelineWidth(timelineContainer.current?.clientWidth || MIN_TIMELINE_WIDTH);
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timelineContainer && timelineContainer.current]
  );

  return (
    <div className="Toolbar-Editor timeline-row" ref={containerRef}>
      <div className={classnames("Toolbar-Editor__Items", { "Toolbar-Editor__Items__Image": mediaType === IMAGE_TYPE })} ref={timelineContainer}>
        {mediaType === VIDEO_TYPE && (
          <div className="TimelineRuler">
            <TimelineRuler
              preview={preview}
              timelineHeight={timelineHeight}
              timelineWidth={timelineWidth}
            />
          </div>
        )}
        <SelectableElements preview={preview}>
          <TimelineContainer
            preview={preview}
            timelineHeight={Math.max(
              timelineHeight - draggablePositionY - TOOLS_HEIGHT,
              containerRef.current?.offsetHeight || 0
            )}
            timelineWidth={timelineWidth}
          />
        </SelectableElements>
      </div>
    </div>
  );
};

export default ({ preview, ...props }: TimelineProps) => (
  <DragProvider>
    <TrimProvider>
      <ThumbnailProvider preview={preview}>
        <Timeline preview={preview} {...props} />
      </ThumbnailProvider>
    </TrimProvider>
  </DragProvider>
);
